/**
 * InvoiceReminderPdf — Phase 101co (Feb 2026)
 *
 * PDF de relance de paiement pour une facture émise et impayée.
 * Trois niveaux (norme suisse) :
 *   • RAPPEL_1        : rappel de courtoisie, ton amical
 *   • RAPPEL_2        : deuxième rappel, ton plus ferme
 *   • MISE_EN_DEMEURE : dernière étape avant recouvrement / poursuite
 *
 * Structure :
 *   • Page 1 : en-tête émetteur + adresse débiteur fenêtre B5 + corps + tableau
 *              récapitulatif de la facture (référence, dates, montants).
 *   • Page 2 : QR-Bill (identique à la facture — même IBAN, même référence),
 *              le montant du QR-Bill reflète le reste dû.
 *
 * Props :
 *   • inv               : la facture (Invoice complète)
 *   • settings          : AppSettings (créancier / logo / etc.)
 *   • qrPngDataUrl      : QR-Bill rasterisé (PNG data URL) — même bulletin que
 *                         la facture, pour que le client puisse payer d'un scan.
 *   • level             : "RAPPEL_1" | "RAPPEL_2" | "MISE_EN_DEMEURE"
 *   • reminderDate      : ISO string — date d'émission de la relance
 */
import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { COMPANY_HEADER } from "../lib/company";

const fmtCHF = (n) => `${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const daysBetween = (fromIso, toIso) => {
  if (!fromIso || !toIso) return null;
  try {
    const from = new Date(fromIso.slice(0, 10));
    const to = new Date(toIso.slice(0, 10));
    return Math.max(0, Math.round((to - from) / 86400000));
  } catch { return null; }
};

const LEVEL_META = {
  RAPPEL_1: {
    title: "1er RAPPEL",
    subtitle: "Rappel de paiement",
    accent: "#8C6B2F",
    tone: "amical",
  },
  RAPPEL_2: {
    title: "2e RAPPEL",
    subtitle: "Second rappel de paiement",
    accent: "#C05C47",
    tone: "ferme",
  },
  MISE_EN_DEMEURE: {
    title: "MISE EN DEMEURE",
    subtitle: "Dernier avis avant recouvrement",
    accent: "#7A1F1F",
    tone: "formel",
  },
};

const buildBodyLines = (level, ctx) => {
  const { ref, issueDate, dueDate, totalTtc, remaining, daysLate } = ctx;
  const montantDu = `${fmtCHF(remaining)} CHF`;
  const echeance = fmtDate(dueDate);
  const retard = daysLate != null ? `${daysLate} jour${daysLate > 1 ? "s" : ""}` : "plusieurs jours";

  if (level === "RAPPEL_1") {
    return [
      "Madame, Monsieur,",
      "",
      `Sauf erreur de notre part, notre facture N° ${ref} du ${fmtDate(issueDate)} d'un montant de ${fmtCHF(totalTtc)} CHF est arrivée à échéance le ${echeance} et demeure impayée à ce jour.`,
      "",
      `Il est possible que ce paiement vous ait échappé — nous vous prions donc simplement de bien vouloir régler la somme restant due de ${montantDu} dans les meilleurs délais, au moyen du bulletin QR-facture ci-joint.`,
      "",
      "Si votre paiement a été effectué entre-temps, veuillez considérer ce courrier comme sans objet et acceptez nos remerciements.",
      "",
      "Nous vous prions d'agréer, Madame, Monsieur, nos salutations distinguées.",
    ];
  }
  if (level === "RAPPEL_2") {
    return [
      "Madame, Monsieur,",
      "",
      `Malgré notre premier rappel, notre facture N° ${ref} du ${fmtDate(issueDate)} d'un montant de ${fmtCHF(totalTtc)} CHF n'a toujours pas été honorée. L'échéance était fixée au ${echeance}, soit un retard de ${retard}.`,
      "",
      `Nous vous prions de bien vouloir procéder au règlement du solde de ${montantDu} sous 10 jours dès réception de la présente, en utilisant le bulletin QR-facture joint.`,
      "",
      "À défaut de paiement dans ce délai, nous serons contraints d'entamer les démarches nécessaires au recouvrement de notre créance (mise en demeure formelle, puis poursuite), ce que nous préférerions éviter.",
      "",
      "Nous restons à votre disposition pour toute question ou arrangement de paiement.",
      "",
      "Nous vous prions d'agréer, Madame, Monsieur, nos salutations distinguées.",
    ];
  }
  // MISE_EN_DEMEURE
  return [
    "Madame, Monsieur,",
    "",
    `Malgré nos deux précédents rappels restés sans réponse, notre facture N° ${ref} du ${fmtDate(issueDate)} d'un montant de ${fmtCHF(totalTtc)} CHF demeure impayée à ce jour. L'échéance était fixée au ${echeance}, soit un retard de ${retard}.`,
    "",
    `Par la présente, nous vous mettons formellement en demeure de régler la somme de ${montantDu} dans un délai de 10 jours à compter de la réception de ce courrier, en utilisant le bulletin QR-facture joint.`,
    "",
    "À défaut de paiement intégral dans ce délai, nous engagerons sans nouvel avis la procédure de poursuite prévue par la LP suisse (art. 102 CO), avec les frais et intérêts moratoires qui en découleront à votre charge.",
    "",
    "Si vous estimez que cette créance est contestée ou fait l'objet d'un différend, veuillez nous en informer par écrit dans les meilleurs délais.",
    "",
    "Nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations.",
  ];
};

const styles = StyleSheet.create({
  page: { padding: 36, fontFamily: "Helvetica", fontSize: 10, color: "#1A1C1A" },

  headerRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 },
  senderBlock: { flexDirection: "row", alignItems: "flex-start", gap: 10, maxWidth: "58%" },
  senderLogo: { width: 55, height: 55 },
  senderInfo: { flexDirection: "column" },
  senderName: { fontSize: 15, fontFamily: "Helvetica-Bold", marginBottom: 4, letterSpacing: 0.4 },
  senderMeta: { fontSize: 8, color: "#575A57", lineHeight: 1.4 },

  titleRight: { flexDirection: "column", alignItems: "flex-end", maxWidth: "40%" },
  titleMain: { fontSize: 20, fontFamily: "Helvetica-Bold", letterSpacing: 1, color: "#1A1C1A" },
  titleAccent: { fontSize: 9, textTransform: "uppercase", letterSpacing: 1, marginTop: 2 },
  titleDate: { fontSize: 8.5, color: "#575A57", marginTop: 4 },

  // Adresse débiteur position fenêtre B5/C5 (SN 351)
  debtorWindow: {
    position: "absolute",
    top: "48mm",
    left: "115mm",
    width: "85mm",
    height: "45mm",
    padding: 4,
  },
  debtorName: { fontSize: 10, fontFamily: "Helvetica-Bold", marginBottom: 1 },
  debtorLine: { fontSize: 9.5, color: "#1A1C1A", lineHeight: 1.35 },

  bodyBlock: { marginTop: 4, marginBottom: 12 },
  paragraph: { fontSize: 10, lineHeight: 1.55, marginBottom: 3, color: "#1A1C1A" },

  // Bloc récap facture (dette)
  duesBox: {
    marginTop: 6,
    padding: 10,
    borderLeft: "3pt solid #C05C47",
    backgroundColor: "#FAFAF7",
    marginBottom: 10,
  },
  duesTitle: { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#575A57", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 },
  duesRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 2 },
  duesLabel: { fontSize: 9.5, color: "#575A57" },
  duesValue: { fontSize: 9.5, fontFamily: "Helvetica-Bold", color: "#1A1C1A" },
  duesTotal: {
    flexDirection: "row", justifyContent: "space-between",
    paddingTop: 6, marginTop: 4, borderTop: "1pt solid #C05C47",
  },
  duesTotalLabel: { fontSize: 11, fontFamily: "Helvetica-Bold" },
  duesTotalValue: { fontSize: 12, fontFamily: "Helvetica-Bold", color: "#C05C47" },

  signature: { marginTop: 18, fontSize: 10, color: "#1A1C1A" },

  qrPageHint: {
    fontSize: 9, color: "#575A57",
    marginBottom: 12, textAlign: "center",
    borderBottom: "0.5pt dashed #A3A6A3", paddingBottom: 8,
  },
  qrBillBoxPage2: { marginTop: 0, paddingTop: 0 },
  qrBillImage: { width: "100%", height: 105 * 2.835 },
  qrBillMissing: {
    fontSize: 9, color: "#8C6B2F",
    padding: 12, textAlign: "center",
    backgroundColor: "#FBF4E9",
  },

  footer: { position: "absolute", bottom: 24, left: 36, right: 36, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.5pt solid #E5E5E0", paddingTop: 6 },
});

export function InvoiceReminderPdf({ inv, settings, qrPngDataUrl, level = "RAPPEL_1", reminderDate }) {
  if (!inv) return null;
  const meta = LEVEL_META[level] || LEVEL_META.RAPPEL_1;
  const isCH = !inv.debtor_country || inv.debtor_country === "CH" || inv.debtor_country === "Suisse";
  const cAddr = (inv.creditor_address || settings?.company_address || "").split("\n").filter(Boolean);
  const cZip = inv.creditor_postal_code || settings?.company_postal_code || "";
  const cCity = inv.creditor_city || settings?.company_city || "";
  const cPhone = inv.creditor_phone || settings?.company_phone || "";
  const cVat = inv.creditor_vat_number || settings?.company_vat_number || "";

  const totalTtc = Number(inv.total_ttc || 0);
  const paid = Number(inv.paid_amount || 0);
  const remaining = Math.max(0, totalTtc - paid);
  const displayDate = reminderDate || inv.last_reminder_at || new Date().toISOString();
  const daysLate = daysBetween(inv.due_date, displayDate);

  const bodyLines = buildBodyLines(level, {
    ref: inv.reference || "—",
    issueDate: inv.issue_date,
    dueDate: inv.due_date,
    totalTtc,
    remaining,
    daysLate,
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* === EN-TÊTE : Émetteur + Titre RAPPEL === */}
        <View style={styles.headerRow}>
          <View style={styles.senderBlock}>
            {settings?.company_logo_url
              ? <Image src={settings.company_logo_url} style={styles.senderLogo} />
              : <Image src={`${typeof window !== "undefined" ? window.location.origin : ""}/logo-maisonthai-pdf.png`} style={styles.senderLogo} />}
            <View style={styles.senderInfo}>
              <Image
                src={`${typeof window !== "undefined" ? window.location.origin : ""}/company-name-sayonara.png`}
                style={{ width: 149, height: 15, marginBottom: 4 }}
              />
              {cAddr.slice(0, 1).map((line, i) => (
                <Text key={i} style={styles.senderMeta}>{line}</Text>
              ))}
              {cZip && cCity && <Text style={styles.senderMeta}>CH-{cZip} {cCity}</Text>}
              {cPhone && <Text style={styles.senderMeta}>Tél {cPhone}</Text>}
              {cVat && <Text style={styles.senderMeta}>TVA : {cVat}</Text>}
            </View>
          </View>

          <View style={styles.titleRight}>
            <Text style={[styles.titleMain, { color: meta.accent }]}>{meta.title}</Text>
            <Text style={[styles.titleAccent, { color: meta.accent }]}>{meta.subtitle}</Text>
            <Text style={styles.titleDate}>{fmtDate(displayDate)}</Text>
            <Text style={styles.titleDate}>Réf. facture : {inv.reference || "—"}</Text>
          </View>
        </View>

        {/* === Adresse débiteur (fenêtre B5) === */}
        <View style={styles.debtorWindow} data-testid="pdf-reminder-debtor-window">
          {inv.debtor_company ? (
            <>
              <Text style={styles.debtorName}>{inv.debtor_company}</Text>
              {inv.debtor_name ? <Text style={styles.debtorLine}>{inv.debtor_name}</Text> : null}
            </>
          ) : (
            <Text style={styles.debtorName}>{inv.debtor_name || "Client"}</Text>
          )}
          {inv.debtor_contact_name ? <Text style={styles.debtorLine}>{inv.debtor_contact_name}</Text> : null}
          {inv.debtor_address ? <Text style={styles.debtorLine}>{inv.debtor_address}</Text> : null}
          {(inv.debtor_postal_code || inv.debtor_city) && (
            <Text style={styles.debtorLine}>{isCH ? "CH-" : ""}{inv.debtor_postal_code} {inv.debtor_city}</Text>
          )}
          {!isCH && inv.debtor_country ? <Text style={styles.debtorLine}>{inv.debtor_country}</Text> : null}
        </View>

        {/* Spacer sous la zone fenêtre (48mm + 45mm = 93mm) */}
        <View style={{ height: "50mm" }} />

        {/* === Corps du rappel === */}
        <View style={styles.bodyBlock}>
          {bodyLines.map((line, i) => (
            <Text key={i} style={styles.paragraph}>{line || " "}</Text>
          ))}
        </View>

        {/* === Récap facture (dette) === */}
        <View style={styles.duesBox}>
          <Text style={styles.duesTitle}>Récapitulatif de la facture impayée</Text>
          <View style={styles.duesRow}>
            <Text style={styles.duesLabel}>N° facture</Text>
            <Text style={styles.duesValue}>{inv.reference || "—"}</Text>
          </View>
          <View style={styles.duesRow}>
            <Text style={styles.duesLabel}>Date d&apos;émission</Text>
            <Text style={styles.duesValue}>{fmtDate(inv.issue_date)}</Text>
          </View>
          <View style={styles.duesRow}>
            <Text style={styles.duesLabel}>Échéance</Text>
            <Text style={styles.duesValue}>{fmtDate(inv.due_date)}</Text>
          </View>
          {daysLate != null && (
            <View style={styles.duesRow}>
              <Text style={styles.duesLabel}>Retard</Text>
              <Text style={[styles.duesValue, { color: meta.accent }]}>{daysLate} jour{daysLate > 1 ? "s" : ""}</Text>
            </View>
          )}
          <View style={styles.duesRow}>
            <Text style={styles.duesLabel}>Total facturé TTC</Text>
            <Text style={styles.duesValue}>{fmtCHF(totalTtc)} CHF</Text>
          </View>
          {paid > 0 && (
            <View style={styles.duesRow}>
              <Text style={styles.duesLabel}>Déjà payé</Text>
              <Text style={styles.duesValue}>− {fmtCHF(paid)} CHF</Text>
            </View>
          )}
          <View style={styles.duesTotal}>
            <Text style={styles.duesTotalLabel}>Reste dû</Text>
            <Text style={styles.duesTotalValue}>{fmtCHF(remaining)} CHF</Text>
          </View>
        </View>

        <Text style={styles.footer}>
          {meta.title} · Facture {inv.reference} · {COMPANY_HEADER} · Émis le {fmtDate(displayDate)}
        </Text>
      </Page>

      {/* === Page 2 — QR-Bill (même bulletin que la facture) === */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.qrPageHint}>
          Bulletin de paiement — à joindre au règlement (partie détachable).
        </Text>
        <View style={styles.qrBillBoxPage2}>
          {qrPngDataUrl ? (
            <Image src={qrPngDataUrl} style={styles.qrBillImage} />
          ) : (
            <Text style={styles.qrBillMissing}>
              QR-Bill non généré — vérifiez que l&apos;IBAN et l&apos;adresse créancier (rue + NPA + ville) sont renseignés dans Paramètres.
            </Text>
          )}
        </View>
        <Text style={styles.footer}>
          {meta.title} · Facture {inv.reference} · Bulletin de paiement · {COMPANY_HEADER}
        </Text>
      </Page>
    </Document>
  );
}

export default InvoiceReminderPdf;
